// ─── QuestionnaireVerrouBloc ─────────────────────────────────────────────
//
// Chantier 9b, côté cockpit. Trois informations et un bouton :
//   · l'état du pré-questionnaire ;
//   · la date à laquelle il a été transmis ;
//   · « Autoriser une correction », qui rouvre le formulaire pour UNE seule
//     nouvelle transmission.
//
// CE BLOC N'AFFICHE AUCUNE RÉPONSE. Ce n'est pas un oubli : les réponses ont
// leur place dans le bloc « Réponses reçues », juste au-dessus. Ici on ne
// parle que de procédure.
//
// Une demande de correction signalée par la cliente s'affiche, mais ne
// déverrouille rien : c'est Anissa qui décide, et le bouton est le seul
// chemin. Décision d'Anissa du 2026-08-02, arbitrage C.
//
// OÙ IL VIT, ET POURQUOI ÇA COMPTE. Ce bloc est rendu par
// `ClientJourneyPage`, la fiche réellement affichée. Un premier jet le
// posait dans `ClientAppPanel`, qui n'est plus monté depuis que
// `NutritionConsultation` est sortie du routage (App.jsx, phase H du
// 2026-05-10) : le code partait dans le dépôt, se construisait sans erreur,
// et n'atteignait jamais l'écran. Le contrôle du bundle servi l'a montré —
// aucune de ses chaînes ne s'y trouvait.

import { useCallback, useEffect, useState } from 'react';
import { useRef } from 'react';
import {
  ANCRE_VERROU_QUESTIONNAIRE,
  ETATS,
  autoriserCorrection,
  libelleEtat,
  lireEtatQuestionnaire,
} from '../services/questionnaireVerrouillage';
import { useConfirmDialog, ConfirmDialog } from './ConfirmDialog';

/** Date + heure. L'heure compte : deux transmissions du même jour se lisent. */
function quand(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-CH', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const PASTILLE = {
  [ETATS.NON_TRANSMIS]: { fond: 'rgba(184, 134, 38, 0.10)', encre: '#8a6722', puce: '🟡' },
  [ETATS.VERROUILLE]: { fond: 'var(--jrn-accent-soft)', encre: 'var(--jrn-accent)', puce: '🔒' },
  [ETATS.CORRECTION_AUTORISEE]: { fond: 'rgba(184, 134, 38, 0.10)', encre: '#8a6722', puce: '✎' },
};

export default function QuestionnaireVerrouBloc({ clientId, clientPrenom = '' }) {
  const [verrou, setVerrou] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const { ask, state: confirmState, close: closeConfirm } = useConfirmDialog();

  // Une relecture se demande, elle ne s'appelle pas : l'effet est le seul
  // endroit qui écrit l'état, ce qui évite deux lectures concurrentes dont on
  // ne saurait pas laquelle a répondu en dernier.
  const [relectures, setRelectures] = useState(0);
  const relire = useCallback(() => setRelectures((n) => n + 1), []);

  useEffect(() => {
    let annule = false;
    (async () => {
      if (!clientId) {
        if (!annule) setChargement(false);
        return;
      }
      const r = await lireEtatQuestionnaire(clientId);
      if (annule) return;
      if (r.ok) {
        setVerrou(r.verrou);
        setErreur(null);
      } else {
        setVerrou(null);
        setErreur(r.erreur);
      }
      setChargement(false);
    })();
    return () => { annule = true; };
  }, [clientId, relectures]);

  // L'ancre posée par la cloche de notifications. On amène le bloc à l'écran
  // une fois son état lu — plus tôt, on ferait défiler vers un cadre encore
  // vide, et Anissa verrait « Lecture de l'état… » au lieu de sa réponse.
  const bloc = useRef(null);
  useEffect(() => {
    if (chargement || !bloc.current) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== `#${ANCRE_VERROU_QUESTIONNAIRE}`) return;
    bloc.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Une intention de navigation ne survit pas à l'écran qu'elle a servi :
    // sans cet effacement, un rechargement y replongerait sans raison.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, [chargement]);

  async function onAutoriser() {
    const prenom = clientPrenom || 'la cliente';
    const ok = await ask({
      title: 'Autoriser une correction ?',
      message:
        `${prenom} pourra remplir son questionnaire à nouveau et le renvoyer UNE fois.\n\n`
        + 'Le nouvel envoi remplacera les réponses actuelles, puis le questionnaire '
        + 'se reverrouillera automatiquement.\n\n'
        + 'L\'autorisation est enregistrée à votre nom, avec la date.',
      confirmLabel: 'Autoriser',
    });
    if (!ok) return;

    setEnCours(true);
    const r = await autoriserCorrection(clientId);
    setEnCours(false);
    if (r.ok) {
      setVerrou(r.verrou);
      setErreur(null);
    } else {
      // « sans_objet » : l'état a changé pendant qu'on regardait l'écran. On
      // relit plutôt que d'afficher une erreur — ce n'en est pas une.
      setErreur(r.erreur === 'sans_objet' ? null : r.erreur);
      relire();
    }
  }

  if (!clientId) return null;

  const pastille = verrou ? (PASTILLE[verrou.etat] || PASTILLE[ETATS.NON_TRANSMIS]) : null;

  return (
    <div ref={bloc} id={ANCRE_VERROU_QUESTIONNAIRE} style={{ marginBottom: 'var(--jrn-6)' }}>
      <ConfirmDialog state={confirmState} onClose={closeConfirm} />

      <p className="jrn-label">Verrouillage du pré-questionnaire</p>
      <p style={{
        fontSize: 'var(--jrn-text-sm)',
        color: 'var(--jrn-text-soft)',
        marginTop: 4,
        marginBottom: 'var(--jrn-3)',
        lineHeight: 1.55,
      }}>
        Une fois transmis, le questionnaire n&apos;est plus modifiable par la cliente.
        Vous seule pouvez rouvrir, pour un seul nouvel envoi.
      </p>

      <div className="jrn-surface" style={{ padding: 'var(--jrn-6)' }}>
        {chargement && (
          <p style={{ margin: 0, fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)' }}>
            Lecture de l&apos;état…
          </p>
        )}

        {!chargement && erreur && (
          <p style={{ margin: 0, fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-warn)' }}>
            ⚠ État indisponible. Rechargez la fiche pour réessayer.
          </p>
        )}

        {!chargement && !erreur && verrou && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--jrn-3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                background: pastille.fond,
                color: pastille.encre,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}>
                {pastille.puce} {libelleEtat(verrou.etat)}
              </span>
              {verrou.transmisLe && (
                <span style={{ fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)' }}>
                  transmis le {quand(verrou.transmisLe)}
                </span>
              )}
            </div>

            {verrou.etat === ETATS.NON_TRANSMIS && (
              <p style={{ margin: 0, fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)', lineHeight: 1.55 }}>
                Aucune transmission enregistrée. Le formulaire est ouvert : il se
                verrouillera de lui-même au premier envoi.
              </p>
            )}

            {verrou.demandeeLe && (
              <p style={{ margin: 0, fontSize: 'var(--jrn-text-sm)', color: '#8a6722', lineHeight: 1.55 }}>
                ✎ Correction signalée par la cliente le {quand(verrou.demandeeLe)}.
                Elle attend votre décision — rien ne s&apos;est ouvert tout seul.
              </p>
            )}

            {verrou.etat === ETATS.CORRECTION_AUTORISEE && (
              <p style={{ margin: 0, fontSize: 'var(--jrn-text-sm)', color: 'var(--jrn-text-muted)', lineHeight: 1.55 }}>
                Autorisée le {quand(verrou.autoriseeLe)}
                {verrou.autoriseePar ? ` par ${verrou.autoriseePar}` : ''} — en attente du
                nouvel envoi. Le verrou se repose dès qu&apos;il arrive.
              </p>
            )}

            {verrou.peutAutoriser && (
              <div className="jrn-actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  onClick={onAutoriser}
                  disabled={enCours}
                  className="jrn-btn jrn-btn--soft"
                  title="Rouvre le questionnaire pour UN seul nouvel envoi"
                >
                  {enCours ? 'Autorisation…' : '✎ Autoriser une correction'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
